import React from 'react';
import type { PageSlug } from '../types';
import { useTeamDashboard } from '../TeamDashboardContext';
import ScrudixLogo from '@/components/ScrudixLogo';

interface NavItem {
  slug: PageSlug;
  label: string;
  badge?: string;
  icon: React.ReactNode;
}

const WORKSPACE_NAV: NavItem[] = [
  { 
    slug: 'overview', 
    label: 'Overview', 
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="9" rx="1.5" />
        <rect x="14" y="3" width="7" height="5" rx="1.5" />
        <rect x="14" y="12" width="7" height="9" rx="1.5" />
        <rect x="3" y="16" width="7" height="5" rx="1.5" />
      </svg>
    ) 
  },
  { 
    slug: 'team', 
    label: 'Team', 
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ) 
  },
  { 
    slug: 'budget', 
    label: 'Budget', 
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ) 
  },
  { 
    slug: 'quality', 
    label: 'Quality', 
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <polyline points="9 11 11 13 15 9" />
      </svg>
    ) 
  },
  { 
    slug: 'projects', 
    label: 'Projects', 
    badge: '3', 
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
        <line x1="12" y1="11" x2="12" y2="17" />
        <line x1="9" y1="14" x2="15" y2="14" />
      </svg>
    ) 
  },
  { 
    slug: 'invoices', 
    label: 'Invoices', 
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    ) 
  },
];

const DEV_NAV: NavItem[] = [
  { 
    slug: 'devmode', 
    label: 'Dev Mode', 
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
      </svg>
    ) 
  },
  { 
    slug: 'reports', 
    label: 'Reports', 
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
      </svg>
    ) 
  },
];

const WC_NAV: NavItem[] = [
  { 
    slug: 'workclock', 
    label: 'Live Overview', 
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    ) 
  },
  { 
    slug: 'wcshifts', 
    label: 'Assign Shifts', 
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    ) 
  },
  { 
    slug: 'wccommits', 
    label: 'Commit Feed', 
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="4" />
        <line x1="1.05" y1="12" x2="7" y2="12" />
        <line x1="17" y1="12" x2="22.95" y2="12" />
      </svg>
    ) 
  },
  { 
    slug: 'wcbudget', 
    label: 'Budget Live', 
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ) 
  },
];

const COLLAB_NAV: NavItem[] = [
  { 
    slug: 'discussion', 
    label: 'Discussion Room', 
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    ) 
  },
  { 
    slug: 'settings', 
    label: 'Settings', 
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ) 
  },
];

function cleanMemberName(name: string) {
  if (!name) return '';
  if (name.length > 12 && /^[a-zA-Z0-9_-]+$/.test(name)) {
    if (name.toLowerCase().startsWith('an675')) {
      return 'Aniket Sharma';
    }
    return name.slice(0, 6) + '...';
  }
  return name;
}

function cleanInitials(name: string, initials?: string) {
  const cleanName = cleanMemberName(name);
  if (!cleanName) return initials || 'TM';
  const parts = cleanName.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return cleanName.slice(0, 2).toUpperCase();
}

export default function Sidebar() {
  const { activePage, setActivePage, sidebarCollapsed, toggleSidebar, toggleTheme, theme, projects, isManager, currentMemberRole, currentMemberName, currentMemberInitials } = useTeamDashboard();
  const sidebarRef = React.useRef<HTMLDivElement>(null);
  const navRef = React.useRef<HTMLElement>(null);

  function go(slug: PageSlug) {
    setActivePage(slug);
    if (typeof window !== 'undefined' && window.innerWidth <= 860 && !sidebarCollapsed) {
      toggleSidebar();
    }
  }

  React.useEffect(() => {
    const sidebarEl = sidebarRef.current;
    if (!sidebarEl) return;

    const handleWheel = (e: WheelEvent) => {
      const nav = navRef.current;
      if (!nav) return;

      // Only intercept if nav actually has scrollable overflow
      if (nav.scrollHeight > nav.clientHeight) {
        e.stopPropagation();
        nav.scrollTop += e.deltaY;
        e.preventDefault();
      }
    };

    sidebarEl.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      sidebarEl.removeEventListener('wheel', handleWheel);
    };
  }, []);

  const wcNavItems = isManager
    ? WC_NAV
    : WC_NAV.map(item => {
      if (item.slug === 'workclock') return { ...item, label: 'My Clock' };
      if (item.slug === 'wcshifts') return { ...item, label: 'My Shift' };
      return item;
    });

  return (
    <>
      <div ref={sidebarRef} className={`td-sidebar${sidebarCollapsed ? ' collapsed' : ''}`}>
        {/* Logo / workspace header */}
        <div className="sb-top">
          <div className="lw">
            <ScrudixLogo size="sm" className="sb-logo" />
            <div>
              <div className="lname">Scrudix</div>
            </div>
          </div>
          <span className="lbeta">BETA</span>
        </div>

        <nav className="sb-nav" ref={navRef}>
          {isManager && (
            <>
              {/* WORKSPACE */}
              <div className="sb-sec">Workspace</div>
              {WORKSPACE_NAV.map(item => (
                <button
                  key={item.slug}
                  type="button"
                  className={`ni${activePage === item.slug ? ' active' : ''}`}
                  onClick={() => go(item.slug)}
                >
                  {item.icon}
                  {item.label}
                  {(item.slug === 'projects' || item.badge) && <span className="nbadge">{item.slug === 'projects' ? String(projects.length) : item.badge}</span>}
                </button>
              ))}

              <div className="sb-div" />

              {/* DEVELOPMENT */}
              <div className="sb-sec">Development</div>
              {DEV_NAV.map(item => (
                <button
                  key={item.slug}
                  type="button"
                  className={`ni${activePage === item.slug ? ' active' : ''}`}
                  onClick={() => go(item.slug)}
                >
                  {item.icon}
                  {item.label}
                </button>
              ))}

              <div className="sb-div" />
            </>
          )}

          {/* WORK CLOCK */}
          <div className="sb-sec">Work Clock</div>
          {wcNavItems.map(item => (
            <button
              key={item.slug}
              type="button"
              className={`ni${activePage === item.slug ? ' active' : ''}`}
              onClick={() => go(item.slug)}
            >
              {item.icon}
              {item.label}
            </button>
          ))}

          <div className="sb-div" />

          {/* COLLABORATION */}
          <div className="sb-sec">Collaboration</div>
          {COLLAB_NAV.map(item => (
            <button
              key={item.slug}
              type="button"
              className={`ni${activePage === item.slug ? ' active' : ''}`}
              onClick={() => go(item.slug)}
            >
              {item.icon}
              {item.label}
              {item.slug === 'discussion' && <span className="ni-live">LIVE</span>}
            </button>
          ))}
        </nav>

        {/* Footer */}
        <div className="sb-foot">
          <div className="sb-user">
            <div className="sb-user-av">{cleanInitials(currentMemberName, currentMemberInitials)}</div>
            <div>
              <div className="sb-user-name">{cleanMemberName(currentMemberName)}</div>
              <div className="sb-user-role">{currentMemberRole}</div>
            </div>
          </div>
          <button className="my-dash-btn" onClick={() => window.location.assign('/dashboard')}>
            <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M7 1L1 7h2v5h3v-3h2v3h3V7h2L7 1z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/></svg>
            → My Dashboard
          </button>
          <button className="swbtn" onClick={toggleTheme}>
            {theme === 'dark'
              ? <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M7 2V1M7 12v1M2 7H1M12 7h1M3.3 3.3l-.7-.7M11.4 11.4l-.7-.7M3.3 10.7l-.7.7M11.4 2.6l-.7.7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/><circle cx="7" cy="7" r="2.5" stroke="currentColor" strokeWidth="1.3"/></svg>
              : <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M12 8.6A5 5 0 0 1 5.4 2a5 5 0 1 0 6.6 6.6z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>
            }
            {theme === 'dark' ? 'Light mode' : 'Dark mode'}
          </button>
        </div>
      </div>

      {/* Mobile backdrop */}
      {!sidebarCollapsed && (
        <div
          className="td-sidebar-mobile-backdrop"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}

      {/* Collapse toggle */}
      <button
        className={`sb-collapse-btn${sidebarCollapsed ? ' collapsed-pos' : ' expanded'}`}
        onClick={toggleSidebar}
        aria-label="Toggle sidebar"
      >
        <svg className="cb-arrow" width="9" height="9" viewBox="0 0 10 10" fill="none">
          <path d="M6 2L3 5l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </>
  );
}
